import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Button, Chip, CircularProgress, Collapse, Divider, FormControlLabel,
  IconButton, MenuItem, Paper, Select, Switch, Tab, Tabs, TextField,
  Tooltip, Typography, useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandIcon,
  Save as SaveIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { RootState, PlaybookTemplateType, PlaybookTemplateStepDef } from '../types';
import { fetchClients } from '../store/slices/clientsSlice';
import { PlaybooksService } from '../services/playbooksService';
import RuleSuggestionsPanel from '../components/playbook/RuleSuggestionsPanel';
import { colorUtils } from '../theme';

// ── Chip-input helper ────────────────────────────────────────────────────────

const ChipInput: React.FC<{
  label: string;
  values: string[];
  onChange: (vals: string[]) => void;
  placeholder?: string;
}> = ({ label, values, onChange, placeholder }) => {
  const theme = useTheme();
  const [draft, setDraft] = useState('');

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed && !values.includes(trimmed)) onChange([...values, trimmed]);
    setDraft('');
  };

  return (
    <Box>
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, mb: 0.75, color: theme.palette.text.secondary }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
        {values.map((v) => (
          <Chip
            key={v}
            label={v}
            size="small"
            onDelete={() => onChange(values.filter((x) => x !== v))}
            sx={{ height: 22, fontSize: '0.72rem' }}
          />
        ))}
      </Box>
      <TextField
        size="small"
        placeholder={placeholder ?? 'Type and press Enter'}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
        onBlur={commit}
        sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' }, width: 260 }}
      />
    </Box>
  );
};

// ── Field label ─────────────────────────────────────────────────────────────

const FieldLabel: React.FC<{ label: string; required?: boolean }> = ({ label, required }) => (
  <Typography sx={{ fontSize: '0.71rem', fontWeight: 500, color: 'text.secondary', mb: 0.5, display: 'block', letterSpacing: '0.02em' }}>
    {label}{required && <Box component="span" sx={{ color: 'error.main', ml: '3px' }}>*</Box>}
  </Typography>
);

// ── Step editor row ──────────────────────────────────────────────────────────

type DraftStep = Partial<PlaybookTemplateStepDef> & { _localId: string };

const PHASES = [
  'preparation', 'identification', 'detection', 'detection_and_analysis',
  'assessment', 'containment', 'containment_eradication_recovery',
  'eradication', 'recovery', 'notification', 'reporting',
  'post_incident', 'post_incident_activity',
  'identify', 'protect', 'detect', 'respond',
];
const STEP_TYPES = [
  'technical', 'communication', 'notification',
  'documentation', 'forensic', 'verification', 'escalation',
];

const formatLabel = (s: string) =>
  s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const StepRow: React.FC<{
  step: DraftStep;
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  onChange: (updated: Partial<DraftStep>) => void;
}> = ({ step, isFirst, isLast, onMoveUp, onMoveDown, onDelete, onChange }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(!step.action?.trim());

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.border.main}`,
        borderRadius: 1.5,
        backgroundColor: theme.palette.background.paper,
        overflow: 'hidden',
        transition: 'border-color 0.2s',
        '&:hover': { borderColor: theme.palette.border.strong },
      }}
    >
      {/* ── Header (always visible) ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.5,
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'background-color 0.15s',
          '&:hover': { backgroundColor: theme.palette.interactive.hover },
        }}
        onClick={() => setExpanded((e) => !e)}
      >
        {/* Step number badge */}
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            backgroundColor: colorUtils.withOpacity(theme.palette.primary.main, 0.15),
            border: `1px solid ${colorUtils.withOpacity(theme.palette.primary.main, 0.35)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: theme.palette.primary.main, lineHeight: 1 }}>
            {step.step_number}
          </Typography>
        </Box>

        {/* Phase chip */}
        {step.phase ? (
          <Chip
            label={formatLabel(step.phase)}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.6rem',
              fontWeight: 600,
              backgroundColor: colorUtils.withOpacity(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.light,
              border: `1px solid ${colorUtils.withOpacity(theme.palette.primary.main, 0.25)}`,
              textTransform: 'uppercase',
              letterSpacing: '0.03em',
              flexShrink: 0,
              '& .MuiChip-label': { px: 1, overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 130 },
            }}
          />
        ) : (
          <Typography sx={{ fontSize: '0.72rem', color: theme.palette.text.disabled, flexShrink: 0 }}>
            No phase
          </Typography>
        )}

        {/* Action preview */}
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: '0.82rem',
            color: step.action?.trim() ? theme.palette.text.primary : theme.palette.text.disabled,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {step.action?.trim() || 'No action defined — click to expand'}
        </Typography>

        {/* Required badge */}
        {step.is_required && (
          <Typography sx={{
            fontSize: '0.6rem', fontWeight: 700, color: theme.palette.warning.main,
            flexShrink: 0, letterSpacing: '0.05em', textTransform: 'uppercase',
          }}>
            Required
          </Typography>
        )}

        {/* Controls — stop propagation so clicks don't toggle expand */}
        <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Move up">
            <span>
              <IconButton size="small" disabled={isFirst} onClick={onMoveUp} sx={{ color: theme.palette.text.secondary }}>
                <UpIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Move down">
            <span>
              <IconButton size="small" disabled={isLast} onClick={onMoveDown} sx={{ color: theme.palette.text.secondary }}>
                <DownIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Remove step">
            <IconButton size="small" onClick={onDelete} sx={{ color: theme.palette.error.main }}>
              <DeleteIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Expand chevron */}
        <ExpandIcon
          sx={{
            fontSize: 18,
            color: theme.palette.text.tertiary,
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            flexShrink: 0,
          }}
        />
      </Box>

      {/* ── Expanded form body ── */}
      <Collapse in={expanded} timeout={200}>
        <Divider sx={{ borderColor: theme.palette.border.light }} />
        <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Row 1: Phase + Type + Required */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 160 }}>
              <FieldLabel label="Phase" />
              <Select
                size="small"
                value={step.phase ?? ''}
                onChange={(e) => onChange({ phase: e.target.value })}
                displayEmpty
                sx={{ fontSize: '0.8rem' }}
              >
                {PHASES.map((p) => (
                  <MenuItem key={p} value={p} sx={{ fontSize: '0.8rem' }}>{formatLabel(p)}</MenuItem>
                ))}
              </Select>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 150 }}>
              <FieldLabel label="Type" />
              <Select
                size="small"
                value={step.step_type ?? ''}
                onChange={(e) => onChange({ step_type: e.target.value as DraftStep['step_type'] })}
                displayEmpty
                sx={{ fontSize: '0.8rem' }}
              >
                {STEP_TYPES.map((t) => (
                  <MenuItem key={t} value={t} sx={{ fontSize: '0.8rem' }}>{formatLabel(t)}</MenuItem>
                ))}
              </Select>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <FieldLabel label="Required" />
              <Box sx={{ display: 'flex', alignItems: 'center', height: 40 }}>
                <Switch
                  size="small"
                  checked={step.is_required ?? true}
                  onChange={(e) => onChange({ is_required: e.target.checked })}
                />
              </Box>
            </Box>
          </Box>

          {/* Row 2: Action */}
          <Box>
            <FieldLabel label="Action" required />
            <TextField
              size="small"
              fullWidth
              placeholder="What should the analyst do?"
              value={step.action ?? ''}
              onChange={(e) => onChange({ action: e.target.value })}
              sx={{ '& .MuiInputBase-root': { fontSize: '0.82rem' } }}
            />
          </Box>

          {/* Row 3: What to check + Expected output */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <FieldLabel label="What to check" />
              <TextField
                size="small"
                fullWidth
                placeholder="Indicators or evidence to look for"
                value={step.what_to_check ?? ''}
                onChange={(e) => onChange({ what_to_check: e.target.value })}
                sx={{ '& .MuiInputBase-root': { fontSize: '0.82rem' } }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <FieldLabel label="Expected output" />
              <TextField
                size="small"
                fullWidth
                placeholder="What a successful outcome looks like"
                value={step.expected_output ?? ''}
                onChange={(e) => onChange({ expected_output: e.target.value })}
                sx={{ '& .MuiInputBase-root': { fontSize: '0.82rem' } }}
              />
            </Box>
          </Box>

          {/* Row 4: Conditions */}
          <Box>
            <FieldLabel label="Conditions / trigger" />
            <TextField
              size="small"
              fullWidth
              placeholder="e.g. Only if > 100 users affected, within 72h of awareness"
              value={step.conditions ?? ''}
              onChange={(e) => onChange({ conditions: e.target.value })}
              sx={{ '& .MuiInputBase-root': { fontSize: '0.82rem' } }}
            />
          </Box>

          {/* Row 5: Technology + Responsible */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <FieldLabel label="Technology" />
              <TextField
                size="small"
                fullWidth
                placeholder="e.g. CrowdStrike, Splunk"
                value={step.primary_technology ?? ''}
                onChange={(e) => onChange({ primary_technology: e.target.value })}
                sx={{ '& .MuiInputBase-root': { fontSize: '0.82rem' } }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <FieldLabel label="Responsible" />
              <TextField
                size="small"
                fullWidth
                placeholder="e.g. Tier 2 analyst"
                value={step.responsible ?? ''}
                onChange={(e) => onChange({ responsible: e.target.value })}
                sx={{ '& .MuiInputBase-root': { fontSize: '0.82rem' } }}
              />
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

// ── Main editor ──────────────────────────────────────────────────────────────

let _localCounter = 0;
const newLocalId = () => `_local_${++_localCounter}`;

interface SignalsState {
  title_keywords: string[];
  mitre_tactics: string[];
  mitre_techniques: string[];
  categories: string[];
}

const EMPTY_SIGNALS: SignalsState = { title_keywords: [], mitre_tactics: [], mitre_techniques: [], categories: [] };

const PlaybookTemplateEditorPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isNew = id === undefined;

  const { clients } = useSelector((state: RootState) => state.clients);
  const { user } = useSelector((state: RootState) => state.auth);

  // Tab state
  const [tab, setTab] = useState<'metadata' | 'steps' | 'rules'>(
    searchParams.get('tab') === 'rules' ? 'rules' : 'metadata',
  );

  // Form state
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [version, setVersion] = useState('1.0');
  const [clientId, setClientId] = useState('');
  const [aiFocus, setAiFocus] = useState('');
  const [signals, setSignals] = useState<SignalsState>({ ...EMPTY_SIGNALS });
  const [steps, setSteps] = useState<DraftStep[]>([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  // Loaded template (for edit mode)
  const [template, setTemplate] = useState<PlaybookTemplateType | null>(null);

  useEffect(() => {
    if (clients.length === 0) dispatch(fetchClients() as any);
  }, []);

  // Load existing template
  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    PlaybooksService.getTemplate(id!)
      .then((t) => {
        setTemplate(t);
        setName(t.name);
        setType(t.type);
        setVersion(t.version ?? '1.0');
        setClientId(t.client_id ?? '');
        setAiFocus(t.ai_focus ?? '');
        const sig = t.detection_signals ?? {};
        setSignals({
          title_keywords: sig.title_keywords ?? [],
          mitre_tactics: sig.mitre_tactics ?? [],
          mitre_techniques: sig.mitre_techniques ?? [],
          categories: sig.categories ?? [],
        });
        if (t.steps) {
          setSteps(t.steps.map((s) => ({ ...s, _localId: s.id })));
        }
      })
      .catch((e) => setError(e.message ?? 'Failed to load template'))
      .finally(() => setLoading(false));
  }, [id]);

  const renumberSteps = (arr: DraftStep[]): DraftStep[] =>
    arr.map((s, i) => ({ ...s, step_number: i + 1 }));

  const addStep = () => {
    setSteps((prev) => renumberSteps([
      ...prev,
      {
        _localId: newLocalId(),
        step_number: prev.length + 1,
        phase: 'identification',
        action: '',
        is_required: true,
        step_type: 'technical',
        conditions: '',
        what_to_check: '',
        expected_output: '',
        primary_technology: '',
        responsible: '',
      },
    ]));
  };

  const moveStep = (idx: number, dir: -1 | 1) => {
    setSteps((prev) => {
      const arr = [...prev];
      [arr[idx], arr[idx + dir]] = [arr[idx + dir], arr[idx]];
      return renumberSteps(arr);
    });
  };

  const deleteStep = (idx: number) => {
    setSteps((prev) => renumberSteps(prev.filter((_, i) => i !== idx)));
  };

  const updateStep = (idx: number, patch: Partial<DraftStep>) => {
    setSteps((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const handleSave = async () => {
    if (!name.trim() || !type.trim()) {
      setError('Name and type are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (isNew) {
        const created = await PlaybooksService.createTemplate({
          type: type.trim().toLowerCase().replace(/\s+/g, '_'),
          name: name.trim(),
          version,
          client_id: clientId,
          detection_signals: signals,
          ai_focus: aiFocus.trim() || undefined,
          steps: steps.map((s) => ({
            phase: s.phase,
            action: s.action,
            what_to_check: s.what_to_check,
            expected_output: s.expected_output,
            primary_technology: s.primary_technology,
            responsible: s.responsible,
            is_required: s.is_required,
            step_type: s.step_type,
          })),
        });
        navigate(`/playbooks/templates/${created.id}/edit`, { replace: true });
        setSaved(true);
      } else {
        // Update metadata
        await PlaybooksService.updateTemplate(id!, {
          name: name.trim(),
          version,
          detection_signals: signals,
          ai_focus: aiFocus.trim() || undefined,
        });

        // Sync steps: delete removed, upsert existing/new
        if (template) {
          const originalIds = new Set((template.steps ?? []).map((s) => s.id));
          const currentIds = new Set(steps.filter((s) => !s._localId.startsWith('_local_')).map((s) => s.id!));

          // Delete removed
          for (const oid of Array.from(originalIds)) {
            if (!currentIds.has(oid)) {
              await PlaybooksService.deleteStep(id!, oid);
            }
          }

          // Upsert all current steps
          const finalSteps: PlaybookTemplateStepDef[] = [];
          for (const s of steps) {
            const payload = {
              phase: s.phase,
              action: s.action,
              what_to_check: s.what_to_check,
              expected_output: s.expected_output,
              primary_technology: s.primary_technology,
              responsible: s.responsible,
              conditions: s.conditions,
              is_required: s.is_required,
              step_type: s.step_type,
            };
            if (s._localId.startsWith('_local_')) {
              const created = await PlaybooksService.createStep(id!, payload);
              finalSteps.push(created);
            } else {
              const updated = await PlaybooksService.updateStep(id!, s.id!, payload);
              finalSteps.push(updated);
            }
          }

          // Reorder if needed
          if (finalSteps.length > 0) {
            await PlaybooksService.reorderSteps(id!, finalSteps.map((s) => s.id));
          }

          // Refresh local state
          const refreshed = await PlaybooksService.getTemplate(id!);
          setTemplate(refreshed);
          setSteps((refreshed.steps ?? []).map((s) => ({ ...s, _localId: s.id })));
        }

        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (e: any) {
      setError(e.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={30} />
      </Box>
    );
  }

  const pageTitle = isNew ? 'New Playbook Template' : `Edit: ${name || '…'}`;
  const effectiveClientId = clientId || (user as any)?.client_id || '';

  return (
    <Box sx={{ pt: 10, pb: 3, px: { xs: 2, sm: 3, md: 4 } }}>
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Back to templates">
            <IconButton size="small" onClick={() => navigate('/playbooks/templates')} sx={{ mt: 0.5 }}>
              <BackIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Typography variant="h1" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>{pageTitle}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.75 }}>
          {error && (
            <Typography sx={{ fontSize: '0.78rem', color: theme.palette.error.main }}>{error}</Typography>
          )}
          {saved && (
            <Typography sx={{ fontSize: '0.78rem', color: theme.palette.success.main }}>Saved</Typography>
          )}
          {tab !== 'rules' && (
            <Button
              variant="contained"
              size="small"
              startIcon={saving ? <CircularProgress size={13} color="inherit" /> : <SaveIcon sx={{ fontSize: 15 }} />}
              onClick={handleSave}
              disabled={saving}
              sx={{ fontSize: '0.78rem', height: 34 }}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2.5, borderBottom: `1px solid ${theme.palette.border.main}` }}
        textColor="primary"
        indicatorColor="primary"
      >
        <Tab value="metadata" label="Metadata" sx={{ fontSize: '0.8rem', textTransform: 'none' }} />
        <Tab value="steps" label={`Steps (${steps.length})`} sx={{ fontSize: '0.8rem', textTransform: 'none' }} />
        {!isNew && (
          <Tab value="rules" label="Detection Rules" sx={{ fontSize: '0.8rem', textTransform: 'none' }} />
        )}
      </Tabs>

      {/* Metadata tab */}
      {tab === 'metadata' && (
        <Paper sx={{ border: `1px solid ${theme.palette.border.main}` }} elevation={0}>
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* ── Identity ── */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <FieldLabel label="Template name" required />
                  <TextField
                    fullWidth size="small"
                    placeholder="e.g. Ransomware Response"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.85rem' } }}
                  />
                </Box>
                <Box sx={{ width: 100 }}>
                  <FieldLabel label="Version" />
                  <TextField
                    fullWidth size="small"
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.85rem' } }}
                  />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <FieldLabel label="Type" required />
                  <TextField
                    fullWidth size="small"
                    placeholder="ransomware"
                    value={type}
                    onChange={(e) => setType(e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                    disabled={!isNew}
                    helperText={isNew ? 'Lowercase, underscores only — cannot change after creation.' : undefined}
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.85rem' } }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FieldLabel label="Client" />
                  <Select
                    size="small"
                    fullWidth
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    displayEmpty
                    disabled={!isNew}
                    sx={{ fontSize: '0.85rem' }}
                  >
                    <MenuItem value=""><em>Global — all clients</em></MenuItem>
                    {clients.map((c) => (
                      <MenuItem key={c.id} value={c.id} sx={{ fontSize: '0.85rem' }}>{c.name}</MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ borderColor: theme.palette.border.light }} />

            {/* ── AI Guidance ── */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography sx={{ fontSize: '0.71rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: theme.palette.text.tertiary }}>
                AI Guidance
              </Typography>
              <Box>
                <FieldLabel label="Focus instructions" />
                <TextField
                  fullWidth size="small" multiline rows={3}
                  placeholder="e.g. Focus on lateral movement detection and endpoint containment"
                  value={aiFocus}
                  onChange={(e) => setAiFocus(e.target.value)}
                  sx={{ '& .MuiInputBase-root': { fontSize: '0.82rem' } }}
                />
              </Box>
            </Box>

            <Divider sx={{ borderColor: theme.palette.border.light }} />

            {/* ── Detection Signals ── */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography sx={{ fontSize: '0.71rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: theme.palette.text.tertiary, mb: 0.5 }}>
                  Detection Signals
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                  Used to auto-match incoming alerts to this playbook. Press Enter after each value.
                </Typography>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5 }}>
                <ChipInput
                  label="Title keywords"
                  values={signals.title_keywords}
                  onChange={(v) => setSignals((s) => ({ ...s, title_keywords: v }))}
                  placeholder="e.g. ransomware"
                />
                <ChipInput
                  label="MITRE tactics"
                  values={signals.mitre_tactics}
                  onChange={(v) => setSignals((s) => ({ ...s, mitre_tactics: v }))}
                  placeholder="e.g. TA0040"
                />
                <ChipInput
                  label="MITRE techniques"
                  values={signals.mitre_techniques}
                  onChange={(v) => setSignals((s) => ({ ...s, mitre_techniques: v }))}
                  placeholder="e.g. T1486"
                />
                <ChipInput
                  label="Alert categories"
                  values={signals.categories}
                  onChange={(v) => setSignals((s) => ({ ...s, categories: v }))}
                  placeholder="e.g. malware"
                />
              </Box>
            </Box>

          </Box>
        </Paper>
      )}

      {/* Steps tab */}
      {tab === 'steps' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {steps.length === 0 ? (
            <Box
              sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                py: 6, gap: 1.5,
                border: `1px dashed ${theme.palette.border.main}`,
                borderRadius: 1.5,
              }}
            >
              <Typography sx={{ color: theme.palette.text.secondary, fontSize: '0.88rem', fontWeight: 500 }}>
                No steps yet
              </Typography>
              <Typography sx={{ color: theme.palette.text.tertiary, fontSize: '0.78rem' }}>
                Add steps to define the response workflow for this playbook.
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={addStep}
                sx={{ fontSize: '0.76rem', mt: 0.5 }}
              >
                Add first step
              </Button>
            </Box>
          ) : (
            <>
              {steps.map((s, i) => (
                <StepRow
                  key={s._localId}
                  step={s}
                  isFirst={i === 0}
                  isLast={i === steps.length - 1}
                  onMoveUp={() => moveStep(i, -1)}
                  onMoveDown={() => moveStep(i, 1)}
                  onDelete={() => deleteStep(i)}
                  onChange={(patch) => updateStep(i, patch)}
                />
              ))}
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={addStep}
                sx={{ alignSelf: 'flex-start', fontSize: '0.78rem', mt: 0.5 }}
              >
                Add step
              </Button>
            </>
          )}
        </Box>
      )}

      {/* Rules tab — only for existing templates */}
      {tab === 'rules' && !isNew && template && (
        <RuleSuggestionsPanel templateId={id!} clientId={effectiveClientId} />
      )}
      </Box>
    </Box>
  );
};

export default PlaybookTemplateEditorPage;
